import { Modal, Box, Typography} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { E164PhoneRegex, EmailRegex } from '../../../utils/regex';
import { InputField } from '../../../components/InputController';
import { PhoneNumberInput } from '../../../shared/PhoneNumberInput';
import { useDispatch, useSelector } from 'react-redux';
import { selectOnboardingApplicationState, updatePricingQuote } from '../../../pmui/onboarding/sagas/pmonboardingSlice';
import { UpsertPricingQuoteDto } from '@inosio/payments-sdk';
import { LoadingButton } from '@mui/lab';

export interface SaveQuoteModalProps {
  open: boolean;
  onClose: () => void;
  pricingQuoteUuid: string;
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: {
    xs: 0,
    md: '50%',
  },
  left: {
    xs: 0,
    md: '50%',
  },
  transform: {
    xs: 'none',
    md: 'translate(-50%, -50%)',
  },
  width: {
    xs: '100vw',
    md: 400,
  },
  height: {
    xs: '100vh',
    md: 'auto',
  },
  maxWidth: '100vw',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: {
    xs: 3,
    md: 4,
  },
  borderRadius: {
    xs: 0,
    md: 2,
  },
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',
};

const SaveQuoteModal = (props: SaveQuoteModalProps) => {
  const { open, onClose, pricingQuoteUuid } = props;
  const { isDownloadingPricingQuote } = useSelector(selectOnboardingApplicationState);
  const dispatch = useDispatch();
  

  const formik = useFormik({
    initialValues: {
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required('Company name is required'),
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Email is required'),
      phoneNumber: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]').required('Required Phone Number'),
    }),
    onSubmit: (values) => {
      const updatePayload: UpsertPricingQuoteDto = {
        FirstName: values.firstName,
        LastName: values.lastName,
        Email: values.email,
        Phone: values.phoneNumber,
        CompanyName: values.companyName,
      };

      dispatch({ type: updatePricingQuote.type, payload: { upsertPricingQuoteDto: updatePayload, pricingQuoteUuid: pricingQuoteUuid } });
    },
  });



  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography sx={{ fontFamily: 'Work Sans !important', textAlign: 'center', fontSize: '18px', mb: 6 }}>Enter your details to save your quote</Typography>
        <form onSubmit={formik.handleSubmit}>
          <InputField
            isfullWidth
            label="Company Name"
            name="companyName"
            value={formik.values.companyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errors={formik.errors?.companyName}
            touched={formik.touched?.companyName}
            isError={false}
          />
          <InputField
            isfullWidth
            label="First Name"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errors={formik.errors?.firstName}
            touched={formik.touched?.firstName}
            isError={false}
          />
          <InputField
            isfullWidth
            label="Last Name"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errors={formik.errors?.lastName}
            touched={formik.touched?.lastName}
            isError={false}
          />
          <InputField
            isfullWidth
            label="Email"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errors={formik.errors?.email}
            touched={formik.touched?.email}
            isError={false}
          />
          <PhoneNumberInput
            {...formik.getFieldProps('phoneNumber')}
            label="Phone Number"
            showErrorMessage={!!formik.errors?.phoneNumber && !!formik.touched?.phoneNumber}
            onChange={(phone) => {
              formik.setFieldValue('phoneNumber', phone.startsWith('+') ? phone : `+${phone}`);
            }}
            errorMessage={formik.errors?.phoneNumber}
          />
          <Box mt={6} display="flex" justifyContent="flex-end">
            <LoadingButton
              type="submit"
              variant="contained"
              style={{
                backgroundColor: '#39A2DB',
                color: 'white',

                boxShadow: 'none',
                borderRadius: '24px',
                fontFamily: "'Open Sans', sans-serif",
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'capitalize',
                width: '164px',
                height: '40px',
              }}
              loading={isDownloadingPricingQuote}
            >
              Save Quote
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default SaveQuoteModal;
